import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { helixApi } from '@shared/api/helixApi';
import type { ManagedChannelCode, ManagedChannelType } from '@shared/types/domain';

export const channelVisibilityKey = ['channels', 'visibility'] as const;
export const channelCatalogueKey = ['channels', 'catalogue'] as const;
const emptyTypes: ManagedChannelType[] = [];
export function typeForPath(path: string): ManagedChannelCode | null {
  path = path.split(/[?#]/)[0] ?? path;
  if (path === '/customers-feedback/live-chat') return 'live_chat';
  if (/^\/tickets(?:\/|$)/.test(path)) return 'tickets';
  if (/^\/calls(?:\/|$)/.test(path)) return 'call_logs';
  if (
    /^\/(?:whatsapp(?:\/|$)|inbox\/whatsapp(?:\/|$)|settings\/(?:whatsapp|ersa-start-messages))/.test(
      path,
    )
  )
    return 'whatsapp';
  if (/^\/(?:live-chat(?:\/|$)|inbox\/live-chat(?:\/|$)|settings\/live-chat-mappings)/.test(path))
    return 'live_chat';
  return null;
}
export function pathIsVisible(path: string, types: ManagedChannelType[]) {
  path = path.split(/[?#]/)[0] ?? path;
  const enabled = (code: ManagedChannelCode) => types.some((x) => x.code === code && x.enabled);
  if (path === '/sla') return enabled('tickets') || enabled('whatsapp');
  if (path === '/assigned-to-me')
    return enabled('tickets') || enabled('whatsapp') || enabled('live_chat');
  if (path === '/inbox') return enabled('whatsapp') || enabled('live_chat');
  const code = typeForPath(path);
  return code === null || enabled(code);
}
export function useChannelVisibility() {
  const query = useQuery({
    queryKey: channelVisibilityKey,
    queryFn: helixApi.channelVisibility,
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
  });
  const types = query.data ?? emptyTypes;
  const enabled = useCallback(
    (code: ManagedChannelCode) => types.some((x) => x.code === code && x.enabled),
    [types],
  );
  return {
    ...query,
    types,
    enabled,
    pathVisible: (path: string) => pathIsVisible(path, types),
    revision: types.map((x) => `${x.code}:${x.enabled}`).join('|'),
  };
}
